import React, {
  useEffect, useMemo, useState,
} from 'react';

import {
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import ContentCard from '../../components/ContentCard';
import SkeletonStack from '../../components/SkeletonStack';
import { useGetStaffsQuery } from '../../services/staff';
import { useGetCurrentUserQuery } from '../../services/users';
import { UserGroup } from '../../types/common';
import useUserGroups from '../../utils/react/hooks/useUserGroups';

import {
  ADMIN_LINKS, STAFF_TOOLS, STUDENT_LINKS, STUDENT_TOOLS, TEACHER_LINKS,
} from './constants';
import EventsCard from './EventsCard';
import QuickLinksCard from './QuickLinksCard';
import StaffBirthdaysCard from './StaffBirthdaysCard';
import StudentCalendar from './StudentCalendar';
import ToolsCard from './ToolsCard';
import { EventOption, LinkOption } from './types';
import WelcomeCard from './WelcomeCard';

dayjs.extend(isBetween);

const DashboardPage = () => {
  const userGroups = useUserGroups() || [];

  const [tools, setTools] = useState<LinkOption[]>([]);
  const [quickLinks, setQuickLinks] = useState<LinkOption[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);

  const isStaff = useMemo(() =>
    userGroups.includes(UserGroup.Admins)
    || userGroups.includes(UserGroup.Teachers)
    || userGroups.includes(UserGroup.Managers)
    || userGroups.includes(UserGroup.Owners),
  [userGroups]);

  const { data: currUser, isLoading } = useGetCurrentUserQuery();
  const { data: activeStaff } = useGetStaffsQuery({
    excludeDisabled: true,
  }, { skip: !isStaff });

  useEffect(() => {
    const isAdmin = userGroups.includes(UserGroup.Admins);
    const isTeacher = userGroups.includes(UserGroup.Teachers);
    const isStudent = userGroups.includes(UserGroup.Students);

    if (isAdmin) {
      setTools(STAFF_TOOLS);
      setQuickLinks(ADMIN_LINKS);
    } else if (isTeacher) {
      setTools(STAFF_TOOLS);
      setQuickLinks(TEACHER_LINKS);
    } else if (isStudent) {
      setTools(STUDENT_TOOLS);
      setQuickLinks(STUDENT_LINKS);
    }

    // Display events which are either in the future or within the active date range
    // INSERT UPCOMING EVENTS HERE
    setEvents(([] as EventOption[])
      .filter((event) => dayjs().isBefore(event.date)
      || dayjs().isBetween(event.activeStartDate, event.activeEndDate))
      .sort((a, b) => (a?.date?.valueOf() || 0) - (b?.date?.valueOf() || 0))
      // Temporary limit to 4 events
      .splice(0, 4),
    );
  }, [userGroups]);

  return (
    isLoading ? <SkeletonStack /> : (
      <Grid container spacing={3}>
        <Grid item xs={12}><WelcomeCard /></Grid>
        {userGroups?.includes(UserGroup.Students) &&
          ((currUser?.data.tutorialPermissions || []).length > 0
          || currUser?.data.hasMasterAccess)
          && (
            <Grid item xs={12}>
              <ContentCard variant="outlined" title="Your Schedule">
                <Typography variant="caption">
                  Click a lesson to schedule a makeup!
                  Make sure you do this before the lesson happens.
                </Typography>
                <StudentCalendar />
              </ContentCard>
            </Grid>
          )}
        <Grid item md={8} xs={12}>
          <Stack spacing={3} height="100%">
            {isStaff && (
              <StaffBirthdaysCard staff={activeStaff?.data} />
            )}
            <EventsCard fillHeight events={events} />
          </Stack>
        </Grid>
        <Grid item md={4} xs={12}>
          <Stack spacing={3}>
            <ToolsCard tools={tools} />
            <QuickLinksCard links={quickLinks} fillHeight />
          </Stack>
        </Grid>
      </Grid>
    )
  );
};
export default DashboardPage;
